import { FC, ReactNode, useEffect, useRef, useState } from 'react';
import { BsGeo } from 'react-icons/bs';
import {
    IoChevronForward,
    IoConstructOutline,
    IoCubeOutline,
    IoDocumentsOutline,
    IoDocumentTextOutline,
    IoGitNetworkOutline,
    IoLayersOutline,
    IoPeopleOutline,
    IoPersonCircleOutline,
} from 'react-icons/io5';

import { Anchor, AvatarGroup, Box, Text, Tooltip } from '@mantine/core';
import { useElementSize, useMergedRef } from '@mantine/hooks';
import { CollectionHelpers } from 'src/helpers/CollectionHelpers';

import { CompanyPartner, CompanyProfile } from 'models';
import { CollateralHelpers } from 'helpers/CollateralHelpers';

import { Avatar } from 'components/avatar/Avatar';
import { CollateralCover } from 'components/collateral/CollateralCover';
import { CompanyLogo } from 'components/company-logo/CompanyLogo';
import { useCollateralItems } from 'components/company-profile/hooks/use-collateral-items';
import { useProductsByCompany } from 'components/company-profile/hooks/use-products-by-company';
import { useReferenceDesigns } from 'components/company-profile/hooks/use-reference-designs';
import { useShowCompanyInfo } from 'components/company-profile/hooks/use-show-company-info';
import { IKImage } from 'components/ik-image/IKImage';
import { RTEContent } from 'components/rte-content/RTEContent';
import { Section } from 'components/section/Section';
import { ComponentThumbnail } from 'components/thumbnail/ComponentThumbnail';
import { TipTapText } from 'components/tiptap/TipTapText';
import { useCompanyProfile } from 'hooks/use-company-profile';
import { useProductSearchCount } from 'hooks/use-product-search-count';

import cx from './styles.module.scss';

type Fact = {
    key: string;
    icon: ReactNode;
    label: string;
    count: number;
    preview?: ReactNode;
};

const About: FC<{ company: CompanyProfile }> = ({ company }) => {
    const { showAbout, showMission, showProducts, showProductSeries, shownLocations, shownCaseStudies } =
        useShowCompanyInfo(company);
    const { referenceDesigns } = useReferenceDesigns(company);
    const { products } = useProductsByCompany(company);
    const { items: collateralItems } = useCollateralItems(company);
    const { count: productCount } = useProductSearchCount({ manufacturer: company.id });
    const { ref: aboutSizeRef, height: aboutHeight } = useElementSize();
    const aboutRef = useRef<HTMLDivElement>(null);
    const mergedAboutRef = useMergedRef(aboutSizeRef, aboutRef);

    const [expanded, setExpanded] = useState(false);
    const [isClamped, setIsClamped] = useState(false);

    useEffect(() => {
        const element = aboutRef.current;

        if (element && !expanded) {
            setIsClamped(element.scrollHeight > element.clientHeight + 1);
        }
    }, [aboutHeight, expanded]);

    const partners = (company.partners ?? []).filter((partner) => partner.company || partner.logo);
    const team = company.contactPeople ?? [];
    const productSeries = showProductSeries ? (company.productSeries ?? []) : [];
    const totalProducts = showProducts ? (productCount ?? 0) : 0;

    const facts: Fact[] = [
        {
            key: 'product-series',
            icon: <IoLayersOutline />,
            label: 'Product series',
            count: productSeries.length,
            preview: (
                <Box className={cx.thumbStack}>
                    {productSeries
                        .filter((series) => series.productSeriesImage)
                        .slice(0, 4)
                        .map((series) => (
                            <Tooltip key={series.seriesName} label={series.seriesName}>
                                <Box className={cx.thumb}>
                                    {series.productSeriesImage && (
                                        <IKImage
                                            fileOrId={series.productSeriesImage}
                                            width={64}
                                            alt={series.seriesName}
                                        />
                                    )}
                                </Box>
                            </Tooltip>
                        ))}
                </Box>
            ),
        },
        {
            key: 'products',
            icon: <IoCubeOutline />,
            label: totalProducts === 1 ? 'Product' : 'Products',
            count: totalProducts,
            preview: (
                <Box className={cx.thumbStack}>
                    {products
                        .filter((product) => product.images?.some((image) => image.file))
                        .slice(0, 4)
                        .map((product) => (
                            <Tooltip key={product.id} label={product.name}>
                                <Box className={cx.thumb}>
                                    <ComponentThumbnail component={product} fallbackSize={14} />
                                </Box>
                            </Tooltip>
                        ))}
                </Box>
            ),
        },
        {
            key: 'collateral',
            icon: <IoDocumentsOutline />,
            label: 'Collateral',
            count: collateralItems.length,
            preview: (
                <Box className={cx.thumbStack}>
                    {collateralItems
                        // A play badge would cover the whole thumb
                        .filter((item) => !CollateralHelpers.isPlayable(item.collateral))
                        .slice(0, 4)
                        .map((item) => (
                            <Tooltip key={item.key} label={item.title}>
                                <Box className={cx.thumb}>
                                    <CollateralCover
                                        collateral={item.collateral}
                                        width={64}
                                        alt={item.title}
                                        iconSize={14}
                                    />
                                </Box>
                            </Tooltip>
                        ))}
                </Box>
            ),
        },
        {
            key: 'services',
            icon: <IoConstructOutline />,
            label: company.servicesInfo?.length === 1 ? 'Service' : 'Services',
            count: company.servicesInfo?.length ?? 0,
        },
        {
            key: 'case-studies',
            icon: <IoDocumentTextOutline />,
            label: shownCaseStudies.length === 1 ? 'Case study' : 'Case studies',
            count: shownCaseStudies.length,
        },
        {
            key: 'reference-designs',
            icon: <IoGitNetworkOutline />,
            label: referenceDesigns.length === 1 ? 'Reference design' : 'Reference designs',
            count: referenceDesigns.length,
        },
        {
            key: 'partners',
            icon: <IoPeopleOutline />,
            label: partners.length === 1 ? 'Partner' : 'Partners',
            count: partners.length,
            preview: (
                <Box className={cx.thumbStack}>
                    {partners.slice(0, 4).map((partner) => (
                        <PartnerLogo key={partner.id} partner={partner} />
                    ))}
                </Box>
            ),
        },
        {
            key: 'team',
            icon: <IoPersonCircleOutline />,
            label: 'Team',
            count: team.length,
            preview: (
                <AvatarGroup spacing="xs">
                    {team
                        .filter((person) => person.image)
                        .slice(0, 4)
                        .map((person) => (
                            <Avatar
                                key={person.name}
                                size={32}
                                name={person.name}
                                // The fact list clips overflow, so the tooltip needs a portal
                                tooltip={{ withinPortal: true, position: 'top' }}
                                image={CollectionHelpers.id(person.image)}
                            />
                        ))}
                </AvatarGroup>
            ),
        },
        {
            key: 'contact',
            icon: <BsGeo />,
            label: shownLocations.length === 1 ? 'Location' : 'Locations',
            count: shownLocations.length,
        },
    ]
        // A single location is already in the hero
        .filter((fact) => fact.count > (fact.key === 'contact' ? 1 : 0));

    const mission = showMission && company.mission && (
        <Box component="figure" aria-label="Mission" className={cx.mission}>
            <Text component="blockquote" className={cx.missionText}>
                <TipTapText content={company.mission} />
            </Text>
        </Box>
    );

    if (!showAbout && !mission && !facts.length) {
        return null;
    }

    const factRows = facts.map(({ key, ...fact }) => <FactRow key={key} id={key} {...fact} />);

    const factSummary = facts.length > 0 && (
        <Box className={cx.factSummary}>
            {facts.map((fact) => (
                <Box key={fact.key} component="a" href={`#${fact.key}`} className={cx.factSummaryItem}>
                    <Box component="span" className={cx.factSummaryIcon}>
                        {fact.icon}
                    </Box>
                    <span>
                        <strong>{fact.count}</strong> {fact.label}
                    </span>
                    {fact.preview}
                </Box>
            ))}
        </Box>
    );

    if (!showAbout && !mission) {
        return (
            <Section>
                {factSummary}
                <Box className={cx.factGrid}>{factRows}</Box>
            </Section>
        );
    }

    const missionBesideAbout = showAbout && !facts.length;

    return (
        <Section>
            {factSummary}
            <Box className={cx.aboutLayout}>
                <Box className={cx.aboutColumn}>
                    {showAbout && (
                        <Box>
                            {/* Line clamp (not a height cut) so collapsed text always ends on a whole line */}
                            <Box
                                ref={mergedAboutRef}
                                className={expanded ? cx.about : `${cx.about} ${cx.aboutClamped}`}
                            >
                                <RTEContent
                                    content={company.about}
                                    emptyMessage={<Text c="dimmed">No about added yet.</Text>}
                                />
                            </Box>

                            {(isClamped || expanded) && (
                                <Anchor
                                    component="button"
                                    type="button"
                                    fw={600}
                                    c="black"
                                    mt="xs"
                                    onClick={() => setExpanded((value) => !value)}
                                >
                                    {expanded ? 'Show less' : 'Read more'}
                                </Anchor>
                            )}
                        </Box>
                    )}
                    {!missionBesideAbout && mission}
                </Box>

                {facts.length > 0 && <Box className={cx.factList}>{factRows}</Box>}
                {missionBesideAbout && mission}
            </Box>
        </Section>
    );
};

const FactRow: FC<Omit<Fact, 'key'> & { id: string }> = ({ id, icon, label, count, preview }) => (
    <Box component="a" href={`#${id}`} className={cx.fact}>
        <Box className={cx.factIcon}>{icon}</Box>
        <Text className={cx.factLabel}>{label}</Text>
        {preview}
        <Text className={cx.factCount}>{count}</Text>
        <IoChevronForward className={cx.factChevron} />
    </Box>
);

const PartnerLogo: FC<{ partner: CompanyPartner }> = ({ partner }) => {
    const { company: partnerCompany } = useCompanyProfile(partner.company);

    const name = partnerCompany?.name ?? partner.name ?? '';
    const logos = partnerCompany?.logos ?? { small: partner.logo, large: null };

    if (!logos.small && !logos.large) {
        return null;
    }

    return (
        <Tooltip label={name} disabled={!name}>
            <Box className={cx.thumb}>
                <CompanyLogo logos={logos} width={64} fallback={<Text fz={10}>{name.charAt(0)}</Text>} />
            </Box>
        </Tooltip>
    );
};

export { About };
